import { displayDateUS } from "../../utils/dateUtils";
import { getInvoiceBreakdown, toMoneyNumber, isInformationalLine } from "../../utils/invoiceBillingBreakdown";

function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const STATUS_LABELS = {
  PAID: "Paid",
  UNPAID: "Unpaid",
  OVERDUE: "Overdue",
  PARTIAL: "Partial",
  CANCELLED: "Cancelled",
  REFUNDED: "Refunded",
};

/**
 * Print-friendly invoice HTML aligned with BillDetail layout.
 * @param {object} bill — same shape as BillDetail `bill`
 * @param {{ logoUrl: string; billToName: string; billToAddress: string }} meta
 */
export function buildInvoicePrintHtml(bill, meta) {
  const logoUrl = escapeHtml(meta.logoUrl || "");
  const billToName = escapeHtml(meta.billToName || "");
  const billToAddress = escapeHtml(meta.billToAddress || "").replace(/\n/g, "<br/>");

  const formatDate = (d) => escapeHtml(displayDateUS(d) || "");

  const {
    invoiceLines,
    invoiceSections,
    computedLineSum,
  } = getInvoiceBreakdown(bill);

  const rawDisplaySubtotal =
    bill.subtotal != null && bill.subtotal !== ""
      ? toMoneyNumber(bill.subtotal)
      : bill.lineSubtotal != null && bill.lineSubtotal !== ""
        ? toMoneyNumber(bill.lineSubtotal) + toMoneyNumber(bill.setupChargeAmount)
        : computedLineSum;
  const displaySubtotal = Number.isFinite(rawDisplaySubtotal) ? rawDisplaySubtotal : computedLineSum;

  const rawDiscountAmount = toMoneyNumber(bill.discountAmount ?? bill.discount);
  const discountAmount = Number.isFinite(rawDiscountAmount) ? rawDiscountAmount : 0;
  const hasDiscount = discountAmount > 0;

  const getDiscountLabel = () => {
    if (bill.discountReason && String(bill.discountReason).trim()) return String(bill.discountReason);
    const t = String(bill.discountType || "").toUpperCase();
    const v = toMoneyNumber(bill.discountValue);
    if (t === "PERCENT" || t === "PERCENTAGE") return `${v}% discount`;
    if (t === "FIXED" || t === "FLAT" || t === "AMOUNT") return `$${v.toFixed(2)} discount`;
    return "Discount";
  };

  const paidAmount = toMoneyNumber(bill.paidAmount);
  const totalAmount = toMoneyNumber(bill.totalAmount);
  const balanceDue = totalAmount - paidAmount;
  const isPaid = bill.status === "PAID";

  const taxAmount = toMoneyNumber(bill.tax);

  const statusLabel = STATUS_LABELS[bill.status] || bill.status || "";

  const groups =
    invoiceSections && invoiceSections.length > 0
      ? invoiceSections
      : [{ section: "shared", title: "", lines: invoiceLines }];

  const lineRows = groups
    .map((group) => {
      const header =
        groups.length > 1 && group.title
          ? `<tr>
          <td colspan="4" style="padding:8px 16px;background:#eef2ff;font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#3730a3;">${escapeHtml(group.title)}</td>
        </tr>`
          : "";
      const rows = (group.lines || [])
        .map((line) => {
          const info = isInformationalLine(line);
          return `<tr>
          <td style="padding:12px 16px;">
            <div style="font-weight:600;color:#111827;">${escapeHtml(line.name || "")}</div>
            <div style="font-size:11px;color:#6b7280;margin-top:2px;">${escapeHtml(line.description || "")}</div>
          </td>
          <td style="padding:12px 16px;text-align:right;font-variant-numeric:tabular-nums;">${
            line.quantity == null ? "—" : String(line.quantity)
          }</td>
          <td style="padding:12px 16px;text-align:right;font-variant-numeric:tabular-nums;">${
            line.rate == null ? "—" : `$${toMoneyNumber(line.rate).toFixed(2)}`
          }</td>
          <td style="padding:12px 16px;text-align:right;font-weight:600;font-variant-numeric:tabular-nums;">${
            info ? "—" : `$${toMoneyNumber(line.amount).toFixed(2)}`
          }</td>
        </tr>`;
        })
        .join("");
      return `${header}${rows}`;
    })
    .join("");

  const taxRow =
    taxAmount > 0
      ? `<div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px;">
           <span style="color:#4b5563;">Tax</span>
           <span style="font-weight:500;font-variant-numeric:tabular-nums;">$${taxAmount.toFixed(2)}</span>
         </div>`
      : "";

  const discountRow =
    hasDiscount
      ? `<div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px;color:#059669;">
           <span style="padding-right:8px;">${escapeHtml(getDiscountLabel())}</span>
           <span style="font-weight:500;font-variant-numeric:tabular-nums;flex-shrink:0;">-$${discountAmount.toFixed(2)}</span>
         </div>`
      : "";

  const paidRow =
    paidAmount > 0
      ? `<div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px;color:#059669;">
           <span>Paid</span>
           <span style="font-weight:500;font-variant-numeric:tabular-nums;">$${paidAmount.toFixed(2)}</span>
         </div>`
      : "";

  const bottomLabel = isPaid ? "Total Paid" : "Balance Due";
  const bottomAmount = isPaid ? totalAmount.toFixed(2) : balanceDue.toFixed(2);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Invoice ${escapeHtml(bill.billNumber || "")}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 13px;
      color: #111827;
      margin: 0;
      padding: 16px;
      background: #fff;
    }
    .card {
      max-width: 960px;
      margin: 0 auto;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
    }
    .head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      padding: 16px 24px;
      border-bottom: 2px solid #e5e7eb;
    }
    .logo { height: 96px; width: auto; max-width: 280px; object-fit: contain; }
    .inv-meta { text-align: right; flex: 1; }
    .inv-title { font-size: 22px; font-weight: 700; margin: 0 0 8px 0; letter-spacing: -0.02em; }
    .meta-row { font-size: 13px; margin: 4px 0; }
    .lbl { font-weight: 600; color: #374151; }
    .pill {
      display: inline-block;
      margin-top: 8px;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      background: #f3f4f6;
      color: #374151;
    }
    .billto {
      padding: 18px 28px;
      border-bottom: 1px solid #e5e7eb;
    }
    .billto-lbl {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      color: #374151;
      margin-bottom: 8px;
    }
    .period {
      padding: 14px 28px;
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
      font-size: 12px;
      color: #4b5563;
    }
    table.lines {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    thead th {
      text-align: left;
      padding: 12px 16px;
      background: #f3f4f6;
      border-bottom: 2px solid #d1d5db;
      font-size: 11px;
      font-weight: 600;
      color: #4b5563;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    thead th:nth-child(2), thead th:nth-child(3), thead th:nth-child(4) { text-align: right; }
    tbody td:nth-child(2), tbody td:nth-child(3), tbody td:nth-child(4) { text-align: right; }
    tbody tr { border-bottom: 1px solid #f3f4f6; }
    .totals {
      padding: 24px 28px;
      display: flex;
      justify-content: flex-end;
    }
    .totals-inner { width: 100%; max-width: 320px; }
    .total-big {
      display: flex;
      justify-content: space-between;
      padding-top: 10px;
      margin-top: 8px;
      border-top: 2px solid #d1d5db;
      font-size: 22px;
      font-weight: 700;
    }
    .footer {
      padding: 16px 28px;
      border-top: 1px solid #e5e7eb;
      background: #f9fafb;
      font-size: 11px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="card">
    <div class="head">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="inv-meta">
        <h1 class="inv-title">INVOICE</h1>
        <div class="meta-row"><span class="lbl">INVOICE #</span> ${escapeHtml(bill.billNumber || "")}</div>
        <div class="meta-row"><span class="lbl">DUE DATE</span> ${formatDate(bill.dueDate)}</div>
        <span class="pill">${escapeHtml(statusLabel)}</span>
      </div>
    </div>
    <div class="billto">
      <div class="billto-lbl">Bill To</div>
      <div style="font-size:13px;line-height:1.5;color:#111827;">${billToName}</div>
      <div style="font-size:13px;line-height:1.5;color:#111827;margin-top:4px;">${billToAddress}</div>
    </div>
    <div class="period">
      <div style="font-weight:600;margin-bottom:4px;">Billing Period</div>
      <div>${formatDate(bill.billingPeriodStart)} – ${formatDate(bill.billingPeriodEnd)}</div>
    </div>
    <div style="padding: 0;">
      <table class="lines">
        <thead>
          <tr>
            <th style="width:46%;">Description</th>
            <th style="width:14%;">Qty</th>
            <th style="width:20%;">Rate</th>
            <th style="width:20%;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${lineRows}
        </tbody>
      </table>
    </div>
    <div class="totals">
      <div class="totals-inner">
        <div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px;">
          <span style="color:#4b5563;">Subtotal</span>
          <span style="font-weight:500;font-variant-numeric:tabular-nums;">$${displaySubtotal.toFixed(2)}</span>
        </div>
        ${taxRow}
        ${discountRow}
        ${paidRow}
        <div class="total-big">
          <span>${escapeHtml(bottomLabel)}</span>
          <span style="font-variant-numeric:tabular-nums;">USD $${bottomAmount}</span>
        </div>
      </div>
    </div>
    <div class="footer">
      For any questions regarding this invoice, please contact the Finance Department.
    </div>
  </div>
</body>
</html>`;
}
